import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { getFullLocaleFromConfig, environment, customArrayMerge } from '../default';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { orderConfig } from './order/order';
import { productConfig } from './product/product';
import { dynamicHelp } from './cms/dynamicHelp';
import { paymentMethodsConfig } from './payment';

const tenantId = 'baurde';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: 'BAUR Versand', domain: 'baur.de', appDeeplinkProtocol: 'baurapp' },
    clientId: 'BaurDe',
    i18n: {
      defaultLocale: 'de-DE',
      locales: ['de-DE'],
      languages: ['de'],
    },
    tenantId,
    company: {
      name: 'Baur',
      seoTitleName: 'BAUR',
    },
    header: {
      hasSmallerFontSize: true,
    },
    search: {
      personalizationSegmentSource: 'baur',
      odoscopeToken: 'UOvRn',
      adServer: {
        mode: 'doubleClick',
        adServerClientId: '23213494915',
        slots: {
          skyscraper: {
            adUnitName: 'WideSkyscraper_160x600_Baur',
            adSizes: [[160, 600]],
          },
          banner: {
            adUnitName: '884_250_Productlist',
            adSizes: [
              'fluid',
              [300, 50],
              [300, 100],
              [320, 50],
              [320, 75],
              [320, 100],
              [468, 60],
              [728, 90],
              [884, 250],
            ],
          },
        },
      },
    },
    abTesting: {
      pRankTestId: 'RE_BaurDe_TransactionPrank',
      odoscopeRankedTestId: 'RE_BaurDe_Odoscope',
      appBannerAbTestId:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
          ? '2026_08_app_banner_test'
          : '2026_08_app_banner',
      clientSideEnrollment: {
        activeTestIds: [
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
            ? '2026_3_key_features_test'
            : '2026_3_key_features',
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
            ? '2026_08_new_filter_design_test'
            : '2026_08_new_filter_design',
        ],
      },
    },
    navigation: {
      firstLevel: {
        border: 'grey.main',
      },
      secondLevel: {
        color: 'text.light',
        backgroundColor: '#4A4543',
        border: 'grey.dark',
      },
    },
    middleware: {
      cmsRewrites: {
        exact: ['/', '/storefront', '/storefront-app', '/mafo/unsubscribe', '/uwg7/unsubscribe'],
        prefixes: [
          '/service-hilfe',
          '/lp',
          '/content',
          '/magazin',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    dynamicYieldEnabled: true,
    enableCustomerNrLogin: true,
    headerSlots: {
      payback: true,
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: '/cms-images/$1/',
      },
      assetServerHost: 'https://bilder.baur.de',
    },
    order: {
      ...orderConfig,
      supportedPaths: {
        myAccountAddresses: true,
        myAccountPayback: true,
      },
      registration: {
        minimumAge: 14,
      },
      seals: {
        trustedShops: {
          enabled: true,
          url: 'https://www.trustedshops.de/bewertung/info_X1661D6DE044EDCDBB810489687D380A7.html',
        },
        ehi: {
          enabled: true,
          url: 'https://zertifikat.ehi-siegel.de/de/zertifikat/s/9e3adf842e1b5f22490345c8c1752fc0/',
        },
      },
      assets: {
        premium: {
          getPremiumImage: ({ language, device }: { language: string; device: string }) =>
            `https://bilder.baur.de/empiriecom/io/baurde/${language.split('-')[0]}/BS3_CheckOut_${device}.jpg`,
          getPremiumIcon: () => 'https://bilder.baur.de/empiriecom/io/baurde/de/premiumicon.svg',
        },
      },
      payment: {
        americanExpress: true,
        clickToPay: true,
        invoicePaymentPauseServiceLayerId: 'zahlpause_rechnung_io',
      },
      payback: {
        enabled: true,
        links: {
          partner: 'https://www.payback.de/punktekonto',
          customerData: 'https://www.payback.de/info/mein-payback/einwilligungen',
          bootstrapStaging: 'https://cdn-nonprod.payback.cloud/oi/de/tst1/site-oi/bootstrap.js',
          bootstrapProduction: 'https://cdn.payback.cloud/oi/de/bootstrap.js',
        },
        apiKeys: {
          bootstrapStaging:
            'eyJwYXJ0bmVyIjoiYmF1ciIsImFwaSI6eyJwciI6IjM1NyIsImNyIjoibm90X3VzZWRfYW55bW9yZSIsImJhcHIiOiJiYXJfb2lfZGVfMjRhIiwiYmFjciI6IkFoSlRXRTNXVkg0WmRDa0sifSwiY21zIjp7InByIjoiMzU3IiwiY3IiOiJub3RfdXNlZF9hbnltb3JlIn19ICAg',
          bootstrapProduction:
            'eyJwYXJ0bmVyIjoiYmF1ciIsImFwaSI6eyJwciI6IjM1NyIsImNyIjoibm90X3VzZWRfYW55bW9yZSIsImJhcHIiOiJiYXJfb2lfZGVfMjRhIiwiYmFjciI6IkNXeW1rTjllTDdyRlBhTHEifSwiY21zIjp7InByIjoiMzU3IiwiY3IiOiJub3RfdXNlZF9hbnltb3JlIn19ICAg',
        },
        cardNumberLength: 10,
        cardNumberHintServiceLayerId: 'payback_kartennummer_io',
      },
      hasCo2Compensation: false,
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `http://cloud-service-cms.${tenantId}.svc.cluster.local:3100`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      contact: {
        dynamicHelp,
      },
      newsletterUnsubscribe: {
        fields: [
          {
            id: 'newsletter',
            formType: 'radio',
            options: [
              {
                id: 'frq-daily',
                value: 'frequency|0',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.daily',
              },
              {
                id: 'frq-weekly',
                value: 'frequency|1',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.weekly',
              },
              {
                id: 'frq-bi-weekly',
                value: 'frequency|2',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.biweekly',
              },
              {
                id: 'frq-monthly',
                value: 'frequency|3',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.monthly',
              },
              {
                id: 'unsubscribe',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink: 'https://www.baur.de/service-hilfe/newsletter/abmelden',
        },
      },
      validation: {
        rules: {
          customerId: {
            regex: /^[A-Za-z0-9.\-ßÄÖÜäöü ]*$/,
            errorMessage: {
              id: 'forms.error.no.special.characters',
              defaultMessage: 'Sonderzeichen sind nicht erlaubt',
            },
          },
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
      url: {
        'de-DE': {
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz/datenschutzhinweis',
          potentialActionTarget: 'https://www.baur.de/s/',
        },
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    chatBot: {
      enabled: true,
      key: '418f4be1c3574c2daa41d05ac9603d32c52d03af187b49dc81805ff5eb0a2c33',
      includePaths: ['/service-hilfe'],
      excludePaths: [
        '/service-hilfe/shopping-vorteile',
        '/service-hilfe/newsletter/anmeldung',
        '/service-hilfe/newsletter/abmeldung',
        '/service-hilfe/nachhaltigkeit',
        '/service-hilfe/ueber-uns',
      ],
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
      abTest: '2023_11_Hebel2',
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: {
      appUrl:
        'https://sdnq.adj.st/storefront?adj_t=55kqkm_8v5x1k&adj_deep_link=baurde%3A%2F%2Fstorefront&adj_fallback=https%3A%2F%2Fwww.baur.de%2Flp%2Fapp',
    },
    tracking: {
      categoryIdHomepage: '6196',
      clarityIdStaging: 'vc3my5m8pe',
      clarityIdProduction: 'sqlq73oxeq',
      soastaId: 'FP553-5JWM6-76MK6-QR4XN-3XLRP',
      tagmanagerId: 'GTM-W3MMXTW',
      tagmanagerSrc: 'https://s.baur.de/btm',
      googleSiteVerificationID: ['ATfbFtTz8sjSDlqWCr13kE9BobcWVlJbM2LlVubNlzs'],
      msvalidateID: 'A5FDCE2E03DF648A7B16A2F9CDE6C0DC',
      pinterestRichPinVerificationID: 'a75fa11cb37d4c1eb5e5e3ab4d870d52',
      schemaOrgOrganisation: {
        'de-DE': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.baur.de/',
          name: 'BAUR Versand',
          alternateName: 'BAUR',
          description:
            'BAUR Versand ist ein deutsches E-Commerce-Unternehmen und Teil der Otto Group, das sich auf den Online-Handel mit Mode, Schuhen und Möbeln spezialisiert. BAUR bietet neben dem Online-Shop Dienstleistungen wie Ratenzahlung und einen umfassenden Kundenservice. BAUR ist *nicht* nur ein reiner Online-Shop, sondern ein Universalversender mit langer Tradition.',
          url: 'https://www.baur.de/',
          logo: 'https://www.baur.de/assets/logos/baurde/logo.svg',
          foundingDate: '1925-01-01',
          founder: {
            '@type': 'Person',
            name: 'Dr. h.c. Friedrich Baur',
            description: 'Gründer der Baur Versand GmbH & Co. KG',
            sameAs: 'https://de.wikipedia.org/wiki/Friedrich_Baur_(Unternehmer)',
          },
          location: {
            '@type': 'Place',
            name: 'Burgkunstadt',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Bahnhofstraße 10',
              addressLocality: 'Burgkunstadt',
              addressRegion: 'BY',
              postalCode: '96224',
              addressCountry: 'DE',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+49-9572-5050',
              email: 'service@baur.de',
              availableLanguage: ['de'],
              hoursAvailable: ['Mo-Su 06:00-23:00'],
            },
          ],
          sameAs: [
            'https://www.facebook.com/BAUR',
            'https://www.instagram.com/baur.de/',
            'https://de.pinterest.com/baurversand/',
            'https://www.youtube.com/user/baurversand',
            'https://www.xing.com/pages/baurversand',
            'https://de.linkedin.com/company/baurgruppe',
            'https://de.wikipedia.org/wiki/Baur_Versand',
            'https://www.baur-gruppe.com/de/index.php',
          ],
          legalName: 'BAUR Versand GmbH & Co. KG',
          areaServed: 'DE',
          foundingLocation: 'Burgkunstadt',
          slogan: 'BAUR schreibt man ohne E.',
          parentOrganization: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
        },
      },
    },
    headerInformation: {
      de: {
        description:
          'lll➤ zum Online-Shop lll➤ Über 4.000 Marken ✅ Kauf auf Rechnung & Raten ▷ PAYBACK Punkte sammeln ✅ Neukunden-Rabatt + Gratis Versand',
        title: 'BAUR Online Shop ▷ Mode | Wohnen | Technik & mehr',
        canonical: 'https://www.baur.de',
        robots: 'noodp,index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      tenantId: '1900',
    },
    dynamicYieldTracking: {
      key: 'd21ba946a03714ea5a981986bd6d2e00a6ad17f63c71e66f9b7f046cbab50aab',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'de',
      alternatives: [
        {
          flag: 'at',
          shops: [{ link: 'https://www.ottoversand.at', title: 'ottoversand.at' }],
        },
        {
          flag: 'ch',
          shops: [{ link: 'https://www.ackermann.ch', title: 'ackermann.ch' }],
        },
      ],
    },
    paybackCoupons: {
      img: {
        path: 'https://storage.googleapis.com/emp-common-libs/assets/payback/baurde/',
      },
    },
    lounge: {
      icon: (lang: string) => `https://bilder.baur.de/empiriecom/io/baurde/${lang}/premiumicon.svg`,
    },
    bookings: {
      overview: {
        hasExplicitMaturities: true,
        showOpenPayment: 'installments',
      },
    },
    speedKit: {
      enabled: true,
      scriptUrl: 'https://baur.app.baqend.com/v1/speedkit/install.js',
    },
    ...paymentMethodsConfig,
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
